<script setup lang="ts">
import { useResizeObserver } from "@vueuse/core";
import { Menu } from "floating-vue";
import { type Component, computed, inject, nextTick, provide, ref, watch } from "vue";

import DatePickerInner from "~/components/dumb/DatePickerInner.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import GlobalPopper, { PopperType } from "~/components/globalPopper";
import MobileBottomSheet from "~/components/MobileBottomSheet.vue";
import {
  DROPDOWN_NESTING_LEVEL,
  DROPDOWN_PARENT_ON_AFTER_CLOSE_FUNCTION,
  DROPDOWN_PARENT_ON_AFTER_OPEN_FUNCTION,
  DROPDOWN_REGISTER_SUBDROPDOWN_STATE,
  DROPDOWN_ROOT_REFERENCE_NODE_GETTER,
} from "~/constants/injection";
import { DueDateFieldIcon, StartDateFieldIcon } from "~/icons";
import { EditorMode, Placement } from "~/shared/enums";
import type { DatePickerDate, Recurrence, SimpleDateRange, Task } from "~/shared/types";
import { usePageStore } from "~/stores";

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  // date picker
  value: DatePickerDate;
  block?: boolean;
  heightBlock?: boolean;
  skidding?: number;
  isStartAt?: boolean;
  propagateClickClasses?: string[];
  showRecommendationButton?: boolean;
  hideActions?: boolean;
  propertyTitle?: string;
  titleOverride?: string;
  iconOverride?: Component;
  maxStartDate?: Date;
  preventUnset?: boolean;
  referenceNode?: () => HTMLElement | undefined;
  // recurrence reminder dropdown
  isReminder?: boolean;
  isRecurrence?: boolean;
  remindAt?: string | null;
  recurrence?: Recurrence | null;
  recursNextAt?: string | null;
  tasks?: Task[];
  // common
  disabled?: boolean;
  placement?: Placement;
  distance?: number;
  cover?: boolean;
  editorMode: EditorMode;
  onKeydown?: (event: KeyboardEvent) => void;
  onAfterOpen?: () => void;
  onAfterClose?: () => void;
  itemIndex?: number;
}>();

const emit = defineEmits<{
  // date picker
  select: [selectedDate: string | null];
  selectRange: [selectedRange: SimpleDateRange];
  recommend: [];
  // common
  mouseover: [event: MouseEvent];
  mouseleave: [event: MouseEvent];
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
}>();

const pageStore = usePageStore();

const isMobile = computed(() => pageStore.isMobile);

const isChipMode = computed(
  () => props.editorMode === EditorMode.CHIP || props.editorMode === EditorMode.CHIP_RECOMMENDATION
);
const isContextMenuMode = computed(() => props.editorMode === EditorMode.CONTEXT_MENU);
const isFormMode = computed(() => props.editorMode === EditorMode.FORM);

const placementDefinite = computed(() => props.placement ?? Placement.BOTTOM_LEFT);
const defaultDistance = computed(() => props.distance ?? (props.cover ? 0 : -8));
const calcDistance = ref(defaultDistance.value);

const dropdownRef = ref<InstanceType<typeof GlobalPopper> | null>(null);
const buttonRef = computed(() => dropdownRef.value?.triggerRef);
const recalculateDistance = () => {
  calcDistance.value = defaultDistance.value;
  if (!buttonRef.value || !props.cover) {
    return;
  }
  calcDistance.value -= buttonRef.value.getBoundingClientRect().height;
};
useResizeObserver(buttonRef, recalculateDistance);
watch(() => props.cover, recalculateDistance);
watch(defaultDistance, recalculateDistance);
watch(buttonRef, recalculateDistance, { once: true });

const datePickerInner = ref<InstanceType<typeof DatePickerInner> | null>(null);

const isOpen = ref(false);
const isHidden = ref(false);
const mobileSheet = ref<{ clearSearch: () => void; search: string } | null>(null);

const registerSubdropdownState = inject(DROPDOWN_REGISTER_SUBDROPDOWN_STATE, null);

watch(isOpen, (newValue) => {
  if (registerSubdropdownState && props.itemIndex !== undefined) {
    registerSubdropdownState(props.itemIndex, newValue);
  }
});

const parentOnAfterOpen = inject(DROPDOWN_PARENT_ON_AFTER_OPEN_FUNCTION);
const parentOnAfterClose = inject(DROPDOWN_PARENT_ON_AFTER_CLOSE_FUNCTION);

const parentNestingLevel = inject(DROPDOWN_NESTING_LEVEL, 0);
const currentNestingLevel = parentNestingLevel + 1;
provide(DROPDOWN_NESTING_LEVEL, currentNestingLevel);

const computedReferenceNode = computed(() => {
  if (props.referenceNode) {
    return props.referenceNode();
  }
  if (currentNestingLevel > 2) {
    const rootReferenceNodeGetter = inject(DROPDOWN_ROOT_REFERENCE_NODE_GETTER);
    if (rootReferenceNodeGetter) {
      return rootReferenceNodeGetter();
    }
  }
  return undefined;
});

const onKeydown = (event: KeyboardEvent) => {
  if (!isOpen.value) {
    return;
  }
  const { key } = event;

  props.onKeydown?.(event);

  switch (key) {
    case "Enter":
    case "ArrowUp":
    case "ArrowDown":
    case "ArrowLeft":
    case "ArrowRight":
    case "Shift":
    case "Meta":
    case "Control":
    case "Option":
    case "Alt": {
      break;
    }
    case "Tab": {
      event.preventDefault();
      break;
    }
    default: {
      dropdownRef.value?.hide();
      break;
    }
  }
};

const onShow = async () => {
  isOpen.value = true;
  props.onAfterOpen?.();
  parentOnAfterOpen?.();
  await nextTick();
  datePickerInner.value?.scrollToCurrentDate();
};

const onHide = () => {
  isOpen.value = false;
  datePickerInner.value?.resetSelectionStates();
  props.onAfterClose?.();
  parentOnAfterClose?.();
};

const open = () => {
  if (isMobile.value) {
    if (props.disabled) {
      return;
    }
    isOpen.value = true;
    isHidden.value = false;
    props.onAfterOpen?.();
    parentOnAfterOpen?.();
  } else {
    dropdownRef.value?.show();
  }
};

const close = () => {
  if (isMobile.value) {
    isOpen.value = false;
    isHidden.value = false;
    datePickerInner.value?.resetSelectionStates();
    props.onAfterClose?.();
    parentOnAfterClose?.();
  } else {
    dropdownRef.value?.hide();
  }
};

const onClickRecommendationButton = () => {
  emit("recommend");
  if (isMobile.value) {
    close();
  } else {
    dropdownRef.value?.hide();
  }
};

const closeAndPropagateMaybe = (event: MouseEvent) => {
  close();
  const { clientX, clientY } = event;
  props.propagateClickClasses
    ?.flatMap((e) => Array.from(document.getElementsByClassName(e)))
    .filter((e): e is HTMLElement => e instanceof HTMLElement)
    .forEach((elem) => {
      const { left, top, width, height } = elem.getBoundingClientRect();
      if (!(clientX >= left && clientX <= left + width && clientY >= top && clientY <= top + height)) {
        return;
      }
      elem.click();
    });
};

const mobileSheetTitle = computed(() => {
  if (props.isReminder) {
    return "Set reminder";
  }
  if (props.isRecurrence) {
    return "Set recurrence";
  }
  if (props.titleOverride) {
    return props.titleOverride;
  }
  return `Change ${props.propertyTitle?.toLowerCase()}`;
});

const remindAtValue = computed(() => props.remindAt ?? null);
const recurrenceValue = computed(() => props.recurrence ?? null);
const recursNextAtValue = computed(() => props.recursNextAt ?? null);

defineExpose({
  open,
  close,
  isOpen: computed(() => isOpen.value),
});
</script>

<template>
  <GlobalPopper
    v-if="!isMobile"
    ref="dropdownRef"
    v-bind="$attrs"
    :type="PopperType.DROPDOWN"
    :container="isChipMode ? '#dart-task-creation-modal-wrapper' : isFormMode ? '#dart-form-wrapper' : undefined"
    :reference-node="() => computedReferenceNode"
    :disabled="disabled"
    :placement="placementDefinite"
    :distance="calcDistance"
    :skidding="skidding"
    no-auto-focus
    :theme="`dropdown-${pageStore.theme}`"
    dont-propagate-close
    class="flex"
    :class="{
      'max-w-full grow': block,
      'h-full': heightBlock,
      'cursor-pointer': !disabled,
    }"
    @mouseover="emit('mouseover', $event)"
    @mouseleave="emit('mouseleave', $event)"
    @focus="emit('focus', $event)"
    @blur="emit('blur', $event)"
    @apply-show="onShow"
    @apply-hide="onHide">
    <template #default>
      <DropdownMenuItemContent
        v-if="isContextMenuMode"
        :icon="iconOverride ? iconOverride : isStartAt ? StartDateFieldIcon : DueDateFieldIcon"
        :title="titleOverride ?? `Change ${propertyTitle?.toLowerCase() ?? 'date'}`"
        is-submenu />

      <slot v-else />
    </template>

    <template #popper>
      <Menu no-auto-focus @mousedown.prevent>
        <div
          class="w-72 overflow-hidden rounded-sm border border-md bg-lt focus-ring-none app-drag-none"
          @click.stop
          @keydown.stop="onKeydown">
          <DatePickerInner
            ref="datePickerInner"
            :value="value"
            :show-recommendation-button="showRecommendationButton"
            :max-start-date="maxStartDate"
            :prevent-unset="preventUnset"
            :is-reminder="isReminder"
            :is-recurrence="isRecurrence"
            :remind-at="remindAtValue"
            :recurrence="recurrenceValue"
            :recurs-next-at="recursNextAtValue"
            :tasks="tasks"
            :on-keydown="onKeydown"
            @select="emit('select', $event)"
            @select-range="emit('selectRange', $event)"
            @recommend="onClickRecommendationButton" />
        </div>
      </Menu>
      <Teleport v-if="isOpen" to="body">
        <div class="absolute inset-0" @click="closeAndPropagateMaybe" @keydown.escape="close" />
      </Teleport>
    </template>
  </GlobalPopper>

  <template v-else>
    <div
      v-bind="$attrs"
      class="flex"
      :class="[
        $attrs.class,
        {
          'max-w-full grow': block,
          'h-full': heightBlock,
          'cursor-pointer': !disabled,
        },
      ]"
      @click="open"
      @keydown="open">
      <slot />
    </div>

    <MobileBottomSheet
      ref="mobileSheet"
      :entity="isOpen"
      :hidden="isHidden"
      :title="mobileSheetTitle"
      include-done
      @close="close">
      <DatePickerInner
        ref="datePickerInner"
        :value="value"
        :show-recommendation-button="showRecommendationButton"
        :max-start-date="maxStartDate"
        :prevent-unset="preventUnset"
        :is-reminder="isReminder"
        :is-recurrence="isRecurrence"
        :remind-at="remindAtValue"
        :recurrence="recurrenceValue"
        :recurs-next-at="recursNextAtValue"
        :tasks="tasks"
        :on-keydown="onKeydown"
        @select="emit('select', $event)"
        @select-range="emit('selectRange', $event)"
        @recommend="onClickRecommendationButton" />
    </MobileBottomSheet>
  </template>
</template>

<style>
@reference "#frontend.css";
.v-popper--theme-dropdown .v-popper__arrow-container {
  @apply hidden;
}
.v-popper--theme-dropdown .v-popper__wrapper {
  @apply shadow-[0_10px_15px_-3px_rgb(0_0_0_/_0.1),_0_4px_6px_-4px_rgb(0_0_0_/_0.1)];
}
.v-popper--theme-dropdown .v-popper__inner {
  @apply rounded border-0 bg-transparent;
}

/* Disable original styling of date picker */
.vc-pane-layout,
.vc-pane,
.vc-container {
  @apply border-0 bg-transparent;
}

/* Hover state for chevron icons */
.vc-arrow.vc-prev.vc-focus,
.vc-arrow.vc-next.vc-focus {
  @apply size-5 focus-ring-none hover:bg-md;
}
.vc-header {
  @apply mr-2;
}

/* Chevron icons */
.vc-base-icon {
  @apply size-4 rotate-90 text-md;
}
.vc-header .vc-title {
  @apply ml-[5px] h-5 px-1 text-sm text-md hover:bg-md hover:opacity-100;
}

/* Keep adjacent-month days visible without allowing click-through selection */
.vc-day.is-not-in-month,
.vc-day.is-not-in-month .vc-day-content,
.vc-day.is-not-in-month .vc-highlight,
.vc-day.is-not-in-month .vc-highlights,
.vc-day.is-not-in-month .vc-day-layer {
  @apply pointer-events-none opacity-30;
}
.vc-day.is-not-in-month .vc-highlights,
.vc-day.is-not-in-month .vc-day-layer {
  @apply pointer-events-none opacity-100;
}

/* Keep past dates visible without allowing click-through selection */
.dart-past-date {
  @apply pointer-events-none opacity-100;
}

/* Disable click on non-interactive days */
.dart-non-interactive-days .vc-day,
.dart-non-interactive-days .vc-day-content,
.dart-non-interactive-days .vc-highlight,
.dart-non-interactive-days .vc-highlights,
.dart-non-interactive-days .vc-day-layer {
  @apply pointer-events-none;
}

/* Hide selected day highlight in recurrence/reminder mode */
.dart-recurrence-reminder-mode .vc-highlight-bg-solid {
  @apply bg-transparent;
}
</style>
